/**
 * EmailViewerDialog — click an email row in the CommunicationTimeline to
 * open the full message.
 *
 * Fetches the full body from the API on demand. If the API can only return
 * the preview (e.g. no Graph connection or an automation-authored email),
 * it renders that with a note.
 *
 * HTML bodies from Outlook can contain arbitrary CSS/inline HTML; we render
 * them inside a sandboxed iframe so they can't run scripts or access the
 * parent document. Plain-text bodies get whitespace-preserving formatting.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Check, Copy, ExternalLink, Loader2, Mail, Paperclip } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { communicationsApi } from "@/lib/api"
import { formatDateTime } from "@shared/utils/date"

export function EmailViewerDialog({
  communicationId,
  open,
  onOpenChange,
}: {
  communicationId: string | null
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const q = useQuery({
    queryKey: ["email", communicationId],
    queryFn: () => communicationsApi.getEmail(communicationId!),
    enabled: !!communicationId && open,
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-4">
            <Mail className="text-rc-blue-600 h-4 w-4 shrink-0" />
            <span className="truncate">{q.data?.subject ?? "Email"}</span>
          </DialogTitle>
        </DialogHeader>

        {q.isLoading ? (
          <div className="grid place-items-center py-12">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </div>
        ) : q.error ? (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
            Couldn't load email:{" "}
            {q.error instanceof Error ? q.error.message : String(q.error)}
          </div>
        ) : q.data ? (
          <EmailBody data={q.data} />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

type EmailData = NonNullable<
  Awaited<ReturnType<typeof communicationsApi.getEmail>>
>

function EmailBody({ data }: { data: EmailData }) {
  const toArr = useMemo(() => {
    if (Array.isArray(data.to)) return data.to
    if (typeof data.to === "string") return data.to.split(",").map((s) => s.trim()).filter(Boolean)
    return []
  }, [data.to])

  return (
    <div className="space-y-4">
      {/* Header meta */}
      <div className="text-sm space-y-1 border-b border-border pb-3">
        <MetaRow label="From">
          <span className="font-medium text-foreground">
            {data.fromName ? `${data.fromName} <${data.from}>` : data.from ?? "—"}
          </span>
        </MetaRow>
        {toArr.length > 0 && (
          <MetaRow label="To">
            <span className="text-foreground">{toArr.join(", ")}</span>
          </MetaRow>
        )}
        {data.cc && data.cc.length > 0 && (
          <MetaRow label="Cc">
            <span className="text-foreground">{data.cc.join(", ")}</span>
          </MetaRow>
        )}
        <MetaRow label="Date">
          <span className="text-muted-foreground">{formatDateTime(data.receivedAt)}</span>
        </MetaRow>
        {(data.direction || data.hasAttachments || data.previewOnly) && (
          <div className="flex flex-wrap gap-2 pt-1">
            {data.direction && (
              <Badge variant="muted" className="text-[10px] capitalize">
                {data.direction}
              </Badge>
            )}
            {data.hasAttachments && (
              <Badge variant="muted" className="text-[10px]">
                <Paperclip className="mr-1 h-3 w-3" />
                Attachments
              </Badge>
            )}
            {data.previewOnly && (
              <Badge variant="muted" className="text-[10px]">
                Preview only
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Preview-only note */}
      {data.previewOnly && data.error && (
        <div className="border-amber-200 bg-amber-50 text-amber-900 rounded-md border p-2 text-xs">
          {data.error}
        </div>
      )}

      {/* Body */}
      <EmailBodyRenderer body={data.body} contentType={data.contentType} />

      {/* Open in Outlook */}
      {data.webLink && <OpenInOutlook webLink={data.webLink} />}
    </div>
  )
}

function OpenInOutlook({ webLink }: { webLink: string }) {
  const [copied, setCopied] = useState(false)
  const copyLink = () => {
    void navigator.clipboard.writeText(webLink)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }
  return (
    <div className="border-border flex flex-wrap items-center gap-3 border-t pt-3">
      <a
        href={webLink}
        target="_blank"
        rel="noreferrer"
        className="bg-rc-blue-600 hover:bg-rc-blue-700 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white"
        title="Opens Outlook Web in a new tab."
      >
        Open in Outlook
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
      <button
        type="button"
        onClick={copyLink}
        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
        title="Copy the Outlook link so you can paste it into your desktop Outlook."
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? "Copied" : "Copy link"}
      </button>
      <span className="text-muted-foreground text-[11px]">
        Opens in Outlook Web — no reliable way to force desktop from a browser.
      </span>
    </div>
  )
}

function EmailBodyRenderer({
  body,
  contentType,
}: {
  body: string
  contentType: "html" | "text"
}) {
  const trimmed = (body ?? "").trim()
  if (!trimmed) {
    return (
      <div className="text-muted-foreground border-border rounded-md border border-dashed p-6 text-center text-sm italic">
        This email has no body content.
      </div>
    )
  }

  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(trimmed)
  if (contentType !== "html" || !looksLikeHtml) {
    return (
      <pre className="text-foreground whitespace-pre-wrap break-words text-sm font-sans leading-relaxed">
        {trimmed}
      </pre>
    )
  }

  // For HTML bodies, extract visible text to verify there's actually
  // something to show. If the HTML is basically empty scaffolding (Word
  // wrapper + inline images we can't display), fall back to a plain-text
  // extraction so users see the words.
  const textOnly = htmlToPlainText(trimmed)
  const hasVisibleText = textOnly.replace(/\s+/g, "").length > 4
  if (!hasVisibleText) {
    return (
      <div className="text-muted-foreground border-border rounded-md border border-dashed p-6 text-center text-sm italic">
        This email is mostly images or inline attachments that can't be shown
        in preview. Use “Open in Outlook” below to view it in full.
      </div>
    )
  }

  return <HtmlBody html={trimmed} />
}

function htmlToPlainText(html: string): string {
  // Strip tags and common style/script blocks to estimate visible text length.
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim()
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground w-14 shrink-0">{label}</span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </div>
  )
}

/**
 * Render Outlook HTML in a sandboxed iframe. Sizes to content and blocks
 * script execution, top-level navigation, and same-origin access.
 */
function HtmlBody({ html }: { html: string }) {
  const ref = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    const iframe = ref.current
    if (!iframe) return
    const doc = iframe.contentDocument
    if (!doc) return

    // Sanitise Outlook HTML: strip scripts, neutralise inline cid: images
    // that we can't fetch (they'd render as broken icons), remove Outlook's
    // giant hard-coded widths / colours that break in narrow modals.
    let cleaned = html
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/<link[^>]*>/gi, "")
      .replace(/<meta[^>]*>/gi, "")
      // cid: images — Outlook inline attachments. Without a companion attachment
      // fetch we can't show them. Swap for a placeholder to avoid broken icons.
      .replace(/src=["']cid:[^"']+["']/gi, 'src="" data-cid-stripped="1" style="display:none"')
      // Nuke fixed pixel widths on tables (email templates commonly set 600px)
      .replace(/(<table[^>]*)\swidth=["']?\d+["']?/gi, "$1")
      .replace(/(<td[^>]*)\swidth=["']?\d+["']?/gi, "$1")

    doc.open()
    doc.write(`
      <!doctype html>
      <html><head>
        <meta charset="utf-8">
        <base target="_blank">
        <style>
          html, body {
            margin: 0;
            padding: 12px 14px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            font-size: 14px;
            line-height: 1.55;
            color: #0f172a;
            background: #ffffff;
            word-wrap: break-word;
            overflow-wrap: break-word;
            overflow-x: auto;
          }
          * { max-width: 100% !important; box-sizing: border-box; }
          img { max-width: 100%; height: auto; display: block; }
          img[data-cid-stripped] { display: none !important; }
          table { max-width: 100% !important; width: auto !important; }
          td, th { max-width: 100% !important; word-wrap: break-word; }
          a { color: #2563eb; word-break: break-all; }
          blockquote {
            border-left: 3px solid #cbd5e1;
            margin: 0.75em 0;
            padding-left: 0.9em;
            color: #475569;
          }
          pre { white-space: pre-wrap; word-wrap: break-word; }
          hr { border: 0; border-top: 1px solid #e2e8f0; margin: 1em 0; }
          p { margin: 0 0 0.75em 0; }
          p:last-child { margin-bottom: 0; }
          /* Outlook adds absurd font sizes; clamp to sensible bounds */
          body { font-size: 14px !important; }
          h1, h2, h3, h4, h5, h6 { line-height: 1.3; margin: 0.5em 0; }
        </style>
      </head><body>${cleaned}</body></html>
    `)
    doc.close()

    // Resize the iframe to fit its content. Do it multiple times because
    // images and web fonts load asynchronously.
    const resize = () => {
      if (!iframe.contentDocument) return
      const bodyH = iframe.contentDocument.body?.scrollHeight ?? 0
      const docH = iframe.contentDocument.documentElement?.scrollHeight ?? 0
      const h = Math.max(bodyH, docH)
      iframe.style.height = Math.min(Math.max(h + 8, 240), 1600) + "px"
    }
    resize()
    const timers = [50, 150, 400, 1000, 2500].map((ms) => setTimeout(resize, ms))

    // Also resize when images finish loading
    const imgs = doc.querySelectorAll("img")
    imgs.forEach((img) => {
      img.addEventListener("load", resize)
      img.addEventListener("error", resize)
    })

    return () => timers.forEach(clearTimeout)
  }, [html])

  return (
    <iframe
      ref={ref}
      // allow-same-origin so we can read scrollHeight for auto-resize;
      // NO allow-scripts, so the body still can't run JS.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      className="border-border w-full rounded-md border bg-white"
      style={{ minHeight: "260px" }}
      title="Email body"
    />
  )
}
